import {
  Mention,
  type MentionTriggerChar,
  parseMentionMarkers,
  toMention,
} from '@design/chat/mention-composer';
import type {
  Code,
  InlineCode,
  Link,
  Parent,
  PhrasingContent,
  Root,
  RootContent,
} from 'mdast';
import { toString } from 'mdast-util-to-string';

const MENTION_TRIGGERS = new Set<MentionTriggerChar>(['@', '#']);

const toMentionNode = (mention: Mention): PhrasingContent => ({
  type: 'text',
  value: mention.label,
  data: {
    hName: 'mention',
    hProperties: {
      'data-mention-type': mention.type,
      'data-mention-id': mention.id,
      'data-mention-label': mention.label,
    },
  },
});

const linkToMention = (link: Link): Mention | undefined => {
  const [type, ...id] = link.url.split(':');

  return toMention(type, id.join(':'), toString(link));
};

// Remove the triggers `@`/`#` from the preceding sibling when a mention is detected.
const consumeTrigger = (siblings: RootContent[]): boolean => {
  const previous = siblings.at(-1);

  if (
    previous?.type !== 'text' ||
    !MENTION_TRIGGERS.has((previous.value.at(-1) ?? '') as MentionTriggerChar)
  ) {
    return false;
  }

  previous.value = previous.value.slice(0, -1);

  return true;
};

// Ensure mentions are parsed as links and not text nodes inside backticks.
const splitCode = (node: Code | InlineCode): RootContent[] => {
  const segments = parseMentionMarkers(node.value).filter(
    segment => segment !== ''
  );

  if (!segments.some(segment => typeof segment !== 'string')) {
    return [node];
  }

  const isFenced = node.type === 'code';
  const children = segments.map<PhrasingContent>(segment => {
    if (typeof segment !== 'string') {
      return toMentionNode(segment);
    }

    return isFenced
      ? { type: 'text', value: segment }
      : { type: 'inlineCode', value: segment };
  });

  if (!isFenced) {
    return children;
  }

  return [
    {
      type: 'paragraph',
      data: {
        hName: 'pre',
        hProperties: node.lang ? { className: [`language-${node.lang}`] } : {},
      },
      children,
    },
  ];
};

const transformChildren = (parent: Parent): void => {
  const children: RootContent[] = [];

  for (const child of parent.children) {
    if (child.type === 'code' || child.type === 'inlineCode') {
      children.push(...splitCode(child));
      continue;
    }

    const mention = child.type === 'link' ? linkToMention(child) : undefined;

    if (mention && consumeTrigger(children)) {
      children.push(toMentionNode(mention));
      continue;
    }

    if ('children' in child) {
      transformChildren(child);
    }

    children.push(child);
  }

  parent.children = children;
};

export const remarkMentions = () => (tree: Root) => {
  transformChildren(tree);
};
